<template>
  <div>
    <div class="d-flex justify-content-sm-between">
      <div class="float-left" v-if="averageHR">
        {{ $t('src/components/training/trainingecg.9764', { '1': this.averageHR }) }}
      </div>
      <div class="float-left" v-else></div>
      <div class="dropdown-container">
        <base-dropdown-select
          :options="listScaleX"
          v-model="scaleX"
          class="dropdown-min-select wider index"
        ></base-dropdown-select>

        <base-dropdown-select
          :options="listScaleY"
          v-model="scaleY"
          class="dropdown-min-select index"
        ></base-dropdown-select>

        <base-dropdown-select
          v-if="signalsInfo"
          :options="listChannel"
          v-model="nChannel"
          class="dropdown-min-select bounded wider index"
        ></base-dropdown-select>
      </div>
    </div>

    <div class="d-flex justify-content-around mt-3">
      <button
        type="button"
        class="btn btn-vertical btn-vertical-colorless btn-vertical-with-icon"
        @click="prevPage"
        :disabled="!isCanScrollPrev"
      >
        <svg-icon
          class="svg-icon right-turn"
          :name="'arrow-shaded'"
          :isDisabled="!isCanScrollPrev"
        ></svg-icon>
      </button>
      <div id="canvas-conteiner" class="canvas-conteiner">
        <canvas class="myCanvas" id="myCanvas"></canvas>
        <canvas id="indicator" height="18" width="150" class="canvas-ruler"></canvas>
      </div>
      <button
        type="button"
        class="btn btn-vertical btn-vertical-colorless btn-vertical-with-icon"
        @click="nextPage"
        :disabled="!isCanScrollNext"
      >
        <svg-icon class="svg-icon" :name="'arrow-shaded'" :IsDisabled="!isCanScrollNext"></svg-icon>
      </button>
    </div>

    <div id="eventName" class="position-absolute"></div>
    <canvas id="event" class="" style="position: absolute; left: 64px; top: 50px; z-index: 10"></canvas>
    <canvas
      id="ruler"
      height="18"
      width="150"
      class="canvas-ruler"
      style="position: absolute; left: 64px; top: 50px;"
    ></canvas>
    <div id="labelInfo" class="ecg-label-info"></div>
    <canvas id="tip" height="18" width="150" class="canvas-tip"></canvas>
  </div>
</template>

<script>
import http from '@/modules/http'
import timeDuration from '@/modules/time-duration'
import getFlatBuffers from '@/modules/ecg-viewer.js'
import { wasmFilter } from '@/mixins/wasm-filter.js'
import * as dotnet from '@/assets/dotnet/dotnet.js'

import('@/styles/components/chart.less')

export default {
  name: 'TrainingEcg',
  components: {
    BaseDropdownSelect: () => import('@/base/BaseDropdownSelect.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue')
  },
  mixins: [wasmFilter],
  props: {
    trainingId: {
      type: Number,
      required: true
    },
    timeBegin: {
      type: Number,
      required: false
    },
    events: {
      type: Array,
      required: false
    },
    averageHR: {
      type: Number,
      required: false
    },
    duration: {
      type: Number,
      required: true
    }
  },
  data: function() {
    return {
      loading: false,
      ecg: null,
      localEcg: [],
      leadscount: 1,
      chartY: [],
      localTimeBegin: this.timeBegin,
      extendedEcg: {},
      signalsInfo: null,
      numberOfSeconds: 6,
      startSecond: 0,
      nChannel: '1',
      time: 0,
      lostSamples: [],
      listScaleX: {
        25: `25 ${this.$t('unit.mm/s')}`,
        50: `50 ${this.$t('unit.mm/s')}`,
        75: `75 ${this.$t('unit.mm/s')}`,
        100: `100 ${this.$t('unit.mm/s')}`
      },
      scaleX: '25',
      listScaleY: {
        10: `10 ${this.$t('unit.mm/mv')}`,
        20: `20 ${this.$t('unit.mm/mv')}`,
        40: `40 ${this.$t('unit.mm/mv')}`,
        80: `80 ${this.$t('unit.mm/mv')}`
      },
      scaleY: '10',
      titleSignals: ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'],
      minY: [],
      maxY: [],
      eventsShown: [],
      startDrawX: null,
      startDrawY: null,
      isDown: false,
      moduleInstance: null,
      source: null,
      dest: null,
      dotnet: null,
      indexFilter: null,
      initFilters: null,
      doFiltersDirect: null,
      coefX: 2,
      nbOfSamples: null,
      drawing: false,
      nbDataRecords: null,
      threshold: null,
      average: null,
      median: null
    }
  },
  computed: {
    listChannel() {
      let opt = {}
      // eslint-disable-next-line no-unused-vars
      for (let key in this.signalsInfo) {
        opt[+key + 1] = this.signalsInfo[key].name
      }

      if (this.signalsInfo.length >= 2) {
        opt[this.signalsInfo.length + 1] = this.$t('role.all')
      }
      return opt
    },
    isCanScrollPrev() {
      if (this.startSecond > 0 && !this.drawing && !this.loading) return true
      else return false
    },
    isCanScrollNext() {
      if (this.startSecond + this.numberOfSeconds < this.duration - 1 && !this.drawing && !this.loading)
        return true
      else return false
    }
  },
  watch: {
    timeBegin() {
      const halfNumberOfSeconds = Math.floor(this.numberOfSeconds / 2)
      this.localTimeBegin = Math.max(0, Math.floor(this.timeBegin) - halfNumberOfSeconds)
    },
    async localTimeBegin(newValue, oldValue) {
      if (Math.abs(newValue - oldValue) === 1) {
        this.changeTimeBegin()
      } else {
        this.changeTimeBegin(true)
      }
    },
    scaleX() {
      this.$emit('set:scaleX', this.scaleX)
      this.setScaleX()
    },
    scaleY() {
      this.$emit('set:scaleY', this.scaleY)
      this.setScaleY()
    },
    async nChannel() {
      this.localEcg = []
      this.leadscount = +this.nChannel > this.signalsInfo.length ? this.signalsInfo.length : 1
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        this.localEcg[leadNumber] = []
        const numberOfChannelThatShows = this.leadscount == 1 ? +this.nChannel : leadNumber + 1
        const ecg = await this.getECG(numberOfChannelThatShows)
        this.localEcg[leadNumber].push(...this.getLocalEcg(ecg, leadNumber))
      }
      this.setScaleY()
      this.worker()
    }
  },
  created() {
    document.addEventListener('keydown', this.onKeyDown)
    this.regularWorker = new Worker('/workers/worker.js')
  },
  mounted() {
    this.setNumberOfSecond()
    this.getSignalsInfo().then(data => {
      this.signalsInfo = data
      this.nbDataRecords = this.signalsInfo[0].nbDataRecords
      this.nbOfSamples = this.signalsInfo[0].nbOfSamples
      this.coefX = (250 / this.nbOfSamples) * 4
      this.$emit('get:signalInfo', this.signalsInfo)

      let promiseWasmFilter = new Promise(resolve => {
        this.dotnet = dotnet
        if (this.dotnet.getBootStatus() === 'Standby') {
          this.dotnet.boot()
        }
        resolve()
      })

      promiseWasmFilter
        .then(() => {
          var globalThis = require('globalthis')() // returns native globalThis if compliant
          this.moduleInstance = globalThis.Module
          this.initFilters = this.dotnet.WasmFilter.initFilters
          this.doFiltersDirect = this.dotnet.WasmFilter.doFiltersDirect
        })
        .then(() => {
          this.getECG()
            .then(data => {
              this.localEcg.push(this.getLocalEcg(data, 0))
            })
            .then(() => {
              this.setScaleY()
              this.worker()
            })
        })
    })
  },
  beforeDestroy() {
    this.source && this.freeBuffer(this.source)
    this.dest && this.freeBuffer(this.dest)
  },
  methods: {
    createCanvas() {
      // Канвас для сетки и графика.
      // TODO: сетку и график нужно разделить, чтобы не перерисовывать сетку при изменении графика
      this.canvas = document.getElementById('myCanvas')
      this.ctx = this.canvas.getContext('2d')

      //Канвас для линейки
      this.rulerCanvas = document.getElementById('ruler')
      this.rulerCtx = this.rulerCanvas.getContext('2d')
      this.rulerCtx.strokeStyle = 'grey'
      this.rulerCtx.lineWidth = 1

      //Канвас для всплывающих подсказок у событий
      this.tipCanvas = document.getElementById('tip')
      this.tipCtx = this.tipCanvas.getContext('2d')
      this.tipCanvas.style.display = 'none'

      //Канвас для событий в виде треугольников
      this.eventCanvas = document.getElementById('event')
      this.eventCtx = this.eventCanvas.getContext('2d')

      //Канвас для индикатора загрузки
      this.indicatorLoadingCanvas = document.getElementById('indicator')
      this.indicatorLoadingCtx = this.indicatorLoadingCanvas.getContext('2d')
      const width = (this.canvas.width = this.indicatorLoadingCanvas.width = this.$el.clientWidth - 70)
      let height
      let tempHeight = 0
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        tempHeight = tempHeight + (this.maxY[leadNumber] - this.minY[leadNumber]) * 4 * +this.scaleY + 25
      }
      tempHeight + 25
      height = this.canvas.height = this.indicatorLoadingCanvas.height = tempHeight

      if (height <= 500) {
        document.getElementById('canvas-conteiner').style.height = 'auto'
      } else {
        document.getElementById('canvas-conteiner').style.height = `500px`
      }

      this.eventCanvas.height = height > 500 ? 500 : height
      this.eventCanvas.width = width
      this.rulerCanvas.height = height > 500 ? 500 : height
      this.rulerCanvas.width = width

      this.createIndicatorLoadingView(width, height)
      this.drawGrid(width, height)
      this.drawECGChart()
      this.displayEvent()

      this.eventCanvas.addEventListener('mousedown', this.handleMouseDown)
      this.eventCanvas.addEventListener('mousemove', this.handleMouseMove)
      this.eventCanvas.addEventListener('mouseup', this.handleMouseUp)
      this.eventCanvas.addEventListener('mouseout', this.handleMouseOut)
      this.eventCanvas.addEventListener('mouseleave', this.focusout)
      this.eventCanvas.addEventListener('wheel', this.scroll)
      this.drawing = false
      this.hideIndicator()
    },
    createIndicatorLoadingView(width, height) {
      this.indicatorLoadingCtx.globalAlpha = 0.5
      this.indicatorLoadingCtx.fillStyle = 'white'
      this.indicatorLoadingCtx.beginPath()
      this.indicatorLoadingCtx.moveTo(0, 0)
      this.indicatorLoadingCtx.lineTo(width, 0)
      this.indicatorLoadingCtx.lineTo(width, height)
      this.indicatorLoadingCtx.lineTo(0, height)
      this.indicatorLoadingCtx.fill()
      this.indicatorLoadingCtx.globalAlpha = 1
      this.indicatorLoadingCtx.font = 'normal 18px Source Sans Pro, Arial, sans-serif'
      this.indicatorLoadingCtx.fillStyle = '#000'
      this.indicatorLoadingCtx.fillText(
        `${this.$t('src/base/basespinner.9012')}`,
        this.indicatorLoadingCanvas.width / 2,
        this.indicatorLoadingCanvas.height / 2
      )
    },
    drawGrid(width, height) {
      const d = 20
      const offsetx = 0
      const offsety = 0

      this.ctx.strokeStyle = 'rgba(255,0,0,.2)'
      this.ctx.lineWidth = 1
      this.ctx.font = '15px Arial'

      // Рисуем вертикальную сетку
      for (let i = 0; i <= width / d; i++) {
        const x = i * d + offsetx + 0.5
        this.ctx.beginPath()
        this.ctx.moveTo(x, offsety)
        this.ctx.lineTo(x, height + offsety)
        this.ctx.stroke()
      }
      // Рисуем горизонтальную сетку
      for (let j = 0; j <= height / d; j++) {
        const y = j * d + offsety + 0.5
        this.ctx.beginPath()
        this.ctx.moveTo(offsetx, y)
        this.ctx.lineTo(width - offsetx, y)
        this.ctx.stroke()
      }

      this.ctx.fillStyle = 'rgba(255,0,0,.5)'
      // Рисуем промежуточные точки
      for (let i = 0; i <= width / 4; i++) {
        for (let j = 0; j <= height / 4; j++) {
          const x = offsetx + i * 4
          const y = j * 4
          const xPoint = offsetx + (i * (4 * this.scaleX)) / 25
          const yPoint = (j * (4 * this.scaleY)) / 10

          if (x % 10 !== 0 && y % 10 !== 0) {
            this.ctx.fillRect(offsetx + i * 4, j * 4, 1, 1)
          }
          if ((x - offsetx == 0 || (x - offsetx) % 100 == 0) && (y === 0 || y % 100 == 0)) {
            this.ctx.fillRect(xPoint - 0.5, yPoint - 0.5, 2, 2)
            //Шкала времени
            if (j === 0) {
              this.ctx.font = '11px Arial'
              this.ctx.fillStyle = 'rgb(0,0,0)'

              const time = this.timeString(this.startSecond + i / 25)

              this.ctx.fillText(time, xPoint + 4, yPoint + 13)
              this.ctx.fillStyle = 'rgba(255,0,0,.5)'
            }
          }
        }
      }
    },
    drawECGChart() {
      const offsetx = 0
      let chartYOffset = 20
      this.ctx.strokeStyle = 'rgb(79, 79, 79)'
      this.ctx.font = '16px Arial'
      this.ctx.fillStyle = 'rgb(28, 179, 170)'

      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        if (this.localEcg[leadNumber] === undefined) return
        const subtr = this.localEcg[leadNumber][0].x
        const chartY = this.chartY[leadNumber]
        const minY = this.minY[leadNumber]

        for (let i = 1; i < this.localEcg[leadNumber].length - 1; i++) {
          const x0 = (this.localEcg[leadNumber][i - 1].x - subtr) * this.coefX * +this.scaleX + offsetx
          const x1 = (this.localEcg[leadNumber][i].x - subtr) * this.coefX * +this.scaleX + offsetx
          const y0 =
            chartYOffset +
            chartY -
            (chartY / (this.maxY[leadNumber] - minY)) * (this.localEcg[leadNumber][i - 1].y - minY)
          const y1 =
            chartYOffset +
            chartY -
            (chartY / (this.maxY[leadNumber] - minY)) * (this.localEcg[leadNumber][i].y - minY)

          this.ctx.beginPath()
          this.ctx.moveTo(x0, y0)
          this.ctx.lineTo(x1, y1)
          this.ctx.stroke()
        }
        const titleSignalNumber = this.leadscount > 1 ? leadNumber : +this.nChannel - 1
        this.ctx.fillText(`${this.titleSignals[titleSignalNumber]}`, 30, chartYOffset)
        chartYOffset += this.chartY[leadNumber] + 25
      }
    },
    getChannelKey(leadIndex = null) {
      if (+this.nChannel > this.signalsInfo.length) {
        return leadIndex === null ? 1 : leadIndex + 1
      }
      return +this.nChannel
    },

    setChartY() {
      if (this.maxY[0] === this.minY[0]) {
        this.minY[0] -= 1
        this.maxY[0] += 1
      }
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        this.chartY[leadNumber] = (this.maxY[leadNumber] - this.minY[leadNumber]) * 4 * +this.scaleY
      }
    },
    setNumberOfSecond() {
      this.numberOfSeconds = Math.ceil((this.$el.offsetWidth - 70) / (this.scaleX * 4))
    },
    getSignalsInfo() {
      return new Promise(resolve => {
        http
          .get('/ecg_viewer/' + this.trainingId, {
            params: {
              header: 1
            }
          })
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            console.log(error)
          })
      })
    },
    getECG(channel = this.nChannel, startSecond = this.startSecond, numberOfSeconds = this.numberOfSeconds) {
      this.loading = true
      this.drawing = true
      this.showIndicator()

      this.$emit('change:part', this.startSecond, this.startSecond + this.numberOfSeconds)

      return new Promise(resolve => {
        http
          .get('/ecg_viewer/' + this.trainingId, {
            params: {
              from: startSecond,
              number: numberOfSeconds,
              nChannel: channel
            }
          })
          .then(response => {
            resolve(response.data)
            this.$emit('set:hr', startSecond, startSecond + numberOfSeconds)
          })
          .catch(error => {
            console.log(error)
          })
          .finally(() => {
            this.loading = false
          })
      })
    },
    getLocalEcg(ecg, leadNumber, second = this.startSecond) {
      const ecgUint8Array = new Uint8Array(Object.values(ecg))
      const sig = getFlatBuffers(ecgUint8Array)

      const digitalSignalRange = sig.digitalMaximum() - sig.digitalMinimum()
      const physicalSignalRange = sig.physicalMaximum() - sig.physicalMinimum()
      const signal = sig.dataArray()
      let physicalSignals = new Float32Array(signal.length)

      for (let i = 0; i < signal.length; i++) {
        const phSignal =
          ((signal[i] - sig.digitalMinimum()) / digitalSignalRange) * physicalSignalRange +
          sig.physicalMinimum()
        physicalSignals[i] = +phSignal.toFixed(6)
      }
      if (!this.source) {
        try {
          this.source = this.allocArray(physicalSignals.length)
          this.dest = this.allocArray(physicalSignals.length)
          this.indexFilter = this.initFilters(physicalSignals.length, this.leadscount, this.nbOfSamples)
        } catch {
          this.$emit('reload:ecg')
        }
      }

      try {
        const filteredSignals = this.doWasmFilter(physicalSignals)

        if (second === this.startSecond) {
          this.setMinMax(filteredSignals, leadNumber)
        }

        const chartData = this.buildChartData(filteredSignals, second)

        this.cacheReceivedPoints(chartData.slice(0), leadNumber, second)
        return chartData
      } catch (error) {
        console.error('WASM filter error:', error)
      }
    },
    extractLostSamples(sig) {
      const lostSamples = []
      if (sig.dataLostSamplesLength() > 0) {
        for (let i = 0; i < sig.dataLostSamplesLength(); i++) {
          const ls = sig.dataLostSamples(i)
          lostSamples.push({
            begin: ls.begin(),
            duration: ls.value()
          })
        }
      }
      return lostSamples
    },
    buildChartData(filteredSignals, time = this.time) {
      const chartData = []

      for (let i = 0; i < filteredSignals.length; i++) {
        const diff = Math.abs(filteredSignals[i] - this.median)
        chartData.push({
          x: time,
          y: diff <= this.threshold ? filteredSignals[i] : this.median
        })
        time = Number((time + 0.004).toFixed(5))
      }
      return chartData
    },
    doWasmFilter(data) {
      this.indexFilter = this.initFilters(data.length, 1, this.nbOfSamples)
      const inArray = new Float32Array(data)
      this.copyToArray(this.source, inArray)
      this.doFiltersDirect(this.indexFilter, this.source, this.dest, inArray.length)
      return this.ptrToArray(this.dest, inArray.length)
    },
    cacheReceivedPoints(ecg, leadNumber, second) {
      const channelKey = this.getChannelKey(leadNumber)
      let sec = second
      while (ecg.length > 0) {
        const removed = ecg.splice(0, this.nbOfSamples)
        this.extendedEcg[`${sec}-${channelKey}`] = removed
        sec++
      }
    },
    setScaleY() {
      this.setChartY()
      this.createCanvas()
      if (this.canvas.height <= 500) {
        document.getElementById('canvas-conteiner').style.height = 'auto'
        if (document.getElementById('chart-ecg').clientHeight <= 400) {
          document.getElementById('chart-ecg').parentElement.classList.add('sticky-top')
        } else {
          document.getElementById('chart-ecg').parentElement.classList.remove('sticky-top')
        }
      } else {
        document.getElementById('canvas-conteiner').style.height = `500px`
        document.getElementById('chart-ecg').parentElement.classList.remove('sticky-top')
      }
    },
    async setScaleX() {
      this.setNumberOfSecond()
      this.leadscount = +this.nChannel > this.signalsInfo.length ? this.signalsInfo.length : 1
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        this.localEcg[leadNumber] = []
        const numberOfChannelThatShows = this.leadscount == 1 ? +this.nChannel : leadNumber + 1
        const ecg = await this.getECG(numberOfChannelThatShows)
        this.localEcg[leadNumber].push(...this.getLocalEcg(ecg, leadNumber))
      }
      this.setScaleY()
      this.worker()
    },
    setMinMax(physicalSignals, leadNumber) {
      this.setThreshold(physicalSignals)

      // Фильтрация выбросов, если threshold > 1
      const filteredSignals =
        this.threshold > 1
          ? physicalSignals.filter(point => Math.abs(point - this.median) <= this.threshold)
          : [...physicalSignals]

      // Находим min и max, исключая крайние значения, если они сильно отличаются
      const sorted = [...filteredSignals].sort((a, b) => a - b)
      const min = sorted[0]
      const max = sorted[sorted.length - 1]

      // Проверяем, не являются ли min/max выбросами
      const secondMin = sorted[1]
      const secondMax = sorted[sorted.length - 2]
      const isMaxOutlier = this.getDiff(max, secondMax) > 50
      const isMinOutlier = this.getDiff(min, secondMin) > 50

      // Форматируем и сохраняем значения
      this.maxY[leadNumber] = isMaxOutlier ? secondMax : this.formatScientific(max)
      this.minY[leadNumber] = isMinOutlier ? secondMin : this.formatScientific(min)
      this.setChartY()
    },

    formatScientific(value) {
      return value.toString().includes('e') ? +value.toFixed(2) : value
    },
    setThreshold(physicalSignals) {
      if (!this.threshold && this.threshold < 1) {
        // Отсортируем массив данных
        const sortedData = physicalSignals.slice().sort((a, b) => a - b)

        // Найдем медиану (второй квартиль)
        this.median = sortedData[Math.floor(sortedData.length / 2)]

        // Найдем первый и третий квартили
        const lowerQuartile = sortedData[Math.floor(sortedData.length / 4)]
        const upperQuartile = sortedData[Math.floor((3 * sortedData.length) / 4)]

        // Вычислим IQR (разницу между верхним и нижним квартилями)
        const iqr = upperQuartile - lowerQuartile

        // Определим пороговое значение
        this.threshold = 20 * iqr
      }
    },
    getDiff(a, b) {
      return Math.abs((a - b) / ((a + b) / 2)) * 100
    },
    async changeTimeBegin(isFullRefresh = false) {
      const diff = this.localTimeBegin - this.startSecond
      this.startSecond = this.localTimeBegin
      this.time = this.startSecond

      const channel = +this.nChannel > this.signalsInfo.length ? [1, 2] : [+this.nChannel]

      if (isFullRefresh || !this.hasExistingEcgData(diff)) {
        await this.fullEcgRefresh()
      } else {
        await this.partialEcgUpdate(diff, channel)
      }

      this.setScaleY()
      this.checkAndTriggerWorker(channel)
    },
    async fullEcgRefresh() {
      const ecgPromises = Array.from({ length: this.leadscount }, (_, leadNumber) => {
        const numberOfChannelThatShows = this.leadscount === 1 ? +this.nChannel : leadNumber + 1
        return this.getECG(numberOfChannelThatShows)
      })

      try {
        const ecgs = await Promise.all(ecgPromises)
        this.localEcg = ecgs.map((ecg, leadNumber) => this.getLocalEcg(ecg, leadNumber))
        this.$emit('change:part', this.startSecond, this.startSecond + this.numberOfSeconds)
      } catch (error) {
        console.error('An error occurred while fetching ECG data:', error)
      }
    },

    hasExistingEcgData(diff) {
      const channelKey = this.getChannelKey()
      return diff > 0
        ? !!this.extendedEcg[`${Math.floor(this.startSecond + this.numberOfSeconds - 1)}-${channelKey}`]
        : !!this.extendedEcg[`${Math.floor(this.startSecond)}-${channelKey}`]
    },

    async partialEcgUpdate(diff) {
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        const addedEcg = this.getEcgToAdd(diff, leadNumber)

        if (addedEcg) {
          this.updateEcgData(addedEcg, leadNumber, diff)
          const result = this.localEcg[leadNumber].map(item => item.y)
          this.setMinMax(result, leadNumber)
        } else {
          const numberOfChannelThatShows = this.leadscount === 1 ? +this.nChannel : leadNumber + 1
          const ecg = await this.getECG(numberOfChannelThatShows, this.startSecond, this.numberOfSeconds)

          this.localEcg[leadNumber] = this.getLocalEcg(ecg, leadNumber)
        }
      }
      this.$emit('change:part', this.startSecond, this.startSecond + this.numberOfSeconds)
    },

    getEcgToAdd(diff, leadNumber) {
      const channelKey = this.getChannelKey(leadNumber)
      const key =
        diff > 0
          ? `${Math.floor(this.startSecond + this.numberOfSeconds - 1)}-${channelKey}`
          : `${Math.floor(this.startSecond)}-${channelKey}`

      return this.extendedEcg[key]
    },

    updateEcgData(addedEcg, leadNumber, diff) {
      if (diff > 0) {
        this.localEcg[leadNumber].splice(0, addedEcg.length)
        this.localEcg[leadNumber].push(...addedEcg)
      } else {
        this.localEcg[leadNumber].splice(this.localEcg[leadNumber].length - addedEcg.length, addedEcg.length)
        this.localEcg[leadNumber].unshift(...addedEcg)
      }
    },

    checkAndTriggerWorker() {
      if (+this.nChannel > this.signalsInfo.length) {
        for (let chn = 1; chn <= this.signalsInfo.length; chn++) {
          this.checkAndTriggerForChannel(chn)
        }
      } else {
        this.checkAndTriggerForChannel(+this.nChannel)
      }
    },
    checkAndTriggerForChannel(channel) {
      const nextKey = `${this.startSecond + this.numberOfSeconds + 2}-${channel}`
      const prevKey = `${this.startSecond - 2}-${channel}`

      const shouldLoadNext =
        !this.extendedEcg[nextKey] && this.startSecond + this.numberOfSeconds + 2 < this.duration - 1

      const shouldLoadPrev = !this.extendedEcg[prevKey] && this.startSecond - 2 >= 0

      if (shouldLoadNext || shouldLoadPrev) {
        this.worker()
      }
    },
    displayEvent() {
      this.eventsShown = []
      this.events
        .filter(
          item =>
            (item.begin >= this.startSecond || item.end >= this.startSecond) &&
            item.begin <= this.startSecond + this.numberOfSeconds
        )
        .forEach(event => {
          this.createEventRange(event)
        })
    },
    createEventRange(event) {
      let x = (event.begin - this.startSecond) * 4 * +this.scaleX - 2.5
      const y = this.canvas.height
      const color = this.getEventColor(event.color)

      this.eventsShown.push({ sec: x, event })

      this.ctx.globalAlpha = 1

      if (event.isDuration) {
        const xEnd = Math.min((event.end - this.startSecond) * 4 * +this.scaleX, this.canvas.width)
        this.ctx.globalAlpha = 0.15
        x = Math.max(x, 0)

        this.ctx.fillStyle = color
        this.ctx.beginPath()
        this.ctx.moveTo(x, y)
        this.ctx.lineTo(xEnd, y)
        this.ctx.lineTo(xEnd, 0)
        this.ctx.lineTo(x, 0)
        this.ctx.fill()
      } else {
        if (event.type === 'LostSamples') {
          this.ctx.lineWidth = 2
          this.ctx.strokeStyle = color
          this.ctx.beginPath()
          this.ctx.moveTo(x + 2.6, 0)
          this.ctx.lineTo(x + 2.6, y - 2)
          this.ctx.stroke()
        }
        this.eventCtx.fillStyle = color
        this.eventCtx.beginPath()
        this.eventCtx.moveTo(x - 5, this.eventCanvas.height)
        this.eventCtx.lineTo(x + 5, this.eventCanvas.height)
        this.eventCtx.lineTo(x, this.eventCanvas.height - 6)
        this.eventCtx.fill()
      }
    },
    getEventColor(color) {
      switch (color) {
        case 'warning':
          return '#FAAD14'
        case 'secondary':
          return '#6c757d'
        case 'primary':
          return '#0482dc'
        default:
          return '#F5222D'
      }
    },
    handleMouseDown(e) {
      e.preventDefault()
      e.stopPropagation()

      this.startDrawX = parseInt(e.offsetX)
      this.startDrawY = parseInt(e.offsetY)
      this.isDown = true
      document.getElementById('labelInfo').style.top =
        this.startDrawY + 25 < 0 ? 0 : this.startDrawY + 25 + 'px'
      document.getElementById('labelInfo').style.left = this.startDrawX + 65 + 'px'
    },
    handleMouseUp(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDown = false
      this.rulerCtx.clearRect(0, 0, this.rulerCanvas.width, this.rulerCanvas.height)
      document.getElementById('labelInfo').style.display = 'none'
    },
    handleMouseOut(e) {
      e.preventDefault()
      e.stopPropagation()
      this.isDown = false
    },
    handleMouseMove(e) {
      e.preventDefault()
      e.stopPropagation()
      const mouseX = parseInt(e.offsetX)
      const mouseY = parseInt(e.offsetY)

      this.handleEventDurationHover(mouseX)
      this.handleEventHover(mouseX, mouseY)
      this.handleDrawing(mouseX, mouseY)
    },

    handleEventDurationHover(mouseX) {
      const eventDuration = this.findEventDuration(mouseX)
      const eventName = document.getElementById('eventName')
      if (eventDuration) {
        eventName.innerText =
          eventDuration.event.title +
          ' (' +
          timeDuration(eventDuration.event.begin, eventDuration.event.end) +
          ')'
        eventName.style.display = ''
        eventName.style.color = this.getEventColor(eventDuration.event.color)
        eventName.style.top = '10px'
        eventName.style.left = `${(this.$el.offsetWidth - 315) / 2}px`
      } else {
        eventName.style.display = 'none'
      }
    },
    findEventDuration(mouseX) {
      return this.eventsShown.find(el => {
        if (el.event.isDuration) {
          const xEnd = (el.event.end - this.startSecond) * 4 * +this.scaleX
          const xStart = (el.event.begin - this.startSecond) * 4 * +this.scaleX
          return xStart <= mouseX && xEnd >= mouseX
        }
      }, this)
    },
    handleEventHover(mouseX, mouseY) {
      if (this.isMouseOnEventCanvas(mouseY)) {
        const event = this.findEventByMouseX(mouseX)

        if (event) {
          this.displayEventTip(event, mouseX, mouseY)
        } else {
          this.hideEventTip()
        }
      } else {
        this.hideEventTip()
      }
    },
    isMouseOnEventCanvas(mouseY) {
      return (
        (mouseY <= this.eventCanvas.height &&
          mouseY >= (this.eventCanvas.height > 500 ? 500 : this.eventCanvas.height) - 7) ||
        (mouseY >= 500 && mouseY <= this.chartY[0] + this.chartY[1])
      )
    },
    findEventByMouseX(mouseX) {
      return this.eventsShown.find(el => el.sec <= mouseX + 3 && el.sec >= mouseX - 3)
    },
    displayEventTip(event, mouseX, mouseY) {
      const text = this.getEventTipText(event)
      this.updateTipCanvasPosition(event, text, mouseX, mouseY)
      this.displayTipCanvas(event, text)
    },
    getEventTipText(event) {
      const { title, value, unit, type } = event.event

      let text = title

      if (value) {
        const formattedValue = type === 'LostSamples' ? `(${(+value).toFixed(3).replace('.', ',')})` : value
        text += ` ${formattedValue}${unit || ''}`
      }

      return text
    },
    updateTipCanvasPosition(event, text, mouseX, mouseY) {
      this.tipCanvas.width = this.tipCtx.measureText(text).width + 12
      let left = event.sec + 50 - this.tipCanvas.width / 2
      if (this.canvas.width - mouseX < 100) {
        left = event.sec + 50 - this.tipCanvas.width
      }
      this.tipCanvas.style.left = left + 'px'
      this.tipCanvas.style.top = mouseY + 15 + 'px'
    },
    displayTipCanvas(event, text) {
      this.tipCanvas.style.display = ''
      this.tipCtx.clearRect(0, 0, this.tipCanvas.width, this.tipCanvas.height)
      document.getElementById('tip').style.backgroundColor = this.getEventColor(event.event.color)
      this.tipCtx.font = 'normal 15px Source Sans Pro, Arial, sans-serif'
      this.tipCtx.fillStyle = '#fff'
      this.tipCtx.fillText(text, 5, 15)
    },
    hideEventTip() {
      this.tipCanvas.style.display = 'none'
      this.tipCtx.clearRect(0, 0, this.tipCanvas.width, this.tipCanvas.height)
    },
    handleDrawing(mouseX, mouseY) {
      if (!this.isDown) {
        return
      }

      this.rulerCtx.clearRect(0, 0, this.rulerCanvas.width, this.rulerCanvas.height)

      var width = mouseX - this.startDrawX
      var height = mouseY - this.startDrawY

      this.rulerCtx.fillStyle = 'rgba(89,89,90,0.2)'
      this.rulerCtx.fillRect(this.startDrawX, this.startDrawY, width, height)

      const ms = ((Math.abs(width) * 250) / this.scaleX).toFixed(0)
      const mv = ((Math.abs(height) * 250) / this.scaleY).toFixed(0)
      const msStr =
        ms >= 1000 ? `${(ms / 1000).toFixed(2)} ${this.$tc('unit.sec', 1)}` : `${ms} ${this.$t('unit.ms')}`
      const mvStr =
        mv >= 1000 ? `${(mv / 1000).toFixed(2)} ${this.$t('unit.mv')}` : `${mv} ${this.$t('unit.mkv')}`

      this.displayLabelInfo(msStr, mvStr)
    },
    displayLabelInfo(msStr, mvStr) {
      document.getElementById('labelInfo').innerText = `${msStr} ${mvStr}`
      document.getElementById('labelInfo').style.display = 'block'
    },

    focusout() {
      this.isDown = false
      this.tipCtx.clearRect(0, 0, this.tipCanvas.width, this.tipCanvas.height)
      this.tipCanvas.style.display = 'none'
      this.rulerCtx.clearRect(0, 0, this.rulerCanvas.width, this.rulerCanvas.height)
      document.getElementById('labelInfo').style.display = 'none'
      document.getElementById('eventName').style.display = 'none'
    },
    scroll(e) {
      e.preventDefault()
      document.getElementById('canvas-conteiner').scrollTop += e.deltaY
    },
    nextPage() {
      if (!this.drawing) {
        this.localTimeBegin = this.startSecond + 1
      }
    },
    prevPage() {
      if (!this.drawing) {
        this.localTimeBegin = this.startSecond > 0 ? this.startSecond - 1 : 0
      }
    },
    onKeyDown(e) {
      if (e.keyCode === 39 && this.isCanScrollNext) {
        this.nextPage()
      }
      if (e.keyCode === 37 && this.isCanScrollPrev) {
        this.prevPage()
      }
    },
    timeString(sec) {
      if (sec < 60) {
        return sec.toString()
      } else if (sec < 3600) {
        const minutes = Math.floor(sec / 60)
        const seconds = sec % 60
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      } else {
        const hours = Math.floor(sec / 3600)
        const remainingSec = sec % 3600
        const minutes = Math.floor(remainingSec / 60)
        const seconds = remainingSec % 60
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }
    },
    worker() {
      const url =
        window.location.hostname.indexOf('localhost') === -1
          ? window.location.protocol + '//' + window.location.host + '/api/'
          : 'http://localhost:3030/api/'

      this.regularWorker.onmessage = e => {
        postMessage(e.data.data)
      }
      this.regularWorker.onmessage = e => {
        this.getLocalEcg(e.data.data, e.data.channel - 1, e.data.seconds)
        if (+this.nChannel > this.signalsInfo.length) {
          if (e.data.channel === this.signalsInfo.length) {
            setTimeout(() => {
              this.loading = false
            }, 300)
          }
        } else {
          setTimeout(() => {
            this.loading = false
          }, 300)
        }
      }

      if (+this.nChannel > this.signalsInfo.length) {
        for (let chn = 1; chn <= this.signalsInfo.length; chn++) {
          const nextKey = Math.floor(this.startSecond + this.numberOfSeconds) + '-' + chn
          if (!this.extendedEcg[nextKey] && this.startSecond + this.numberOfSeconds < this.duration - 1) {
            this.loading = true
            this.regularWorker.postMessage({
              url: url,
              jwt: localStorage.getItem('jwt'),
              trainingId: this.trainingId,
              startSecond: Math.max(0, this.startSecond + Math.floor(this.numberOfSeconds)),
              numberOfSeconds: this.numberOfSeconds,
              nChannel: chn
            })
          }

          if (this.startSecond - 1 > 0) {
            const prevKey = Math.floor(this.startSecond - 1) + '-' + chn
            if (!this.extendedEcg[prevKey]) {
              this.loading = true
              this.regularWorker.postMessage({
                url: url,
                jwt: localStorage.getItem('jwt'),
                trainingId: this.trainingId,
                startSecond: Math.max(0, this.startSecond - Math.floor(this.numberOfSeconds)),
                numberOfSeconds: this.numberOfSeconds,
                nChannel: chn
              })
            }
          }
        }
      } else {
        const nextKey = Math.floor(this.startSecond + this.numberOfSeconds) + '-' + +this.nChannel
        if (!this.extendedEcg[nextKey] && this.startSecond + this.numberOfSeconds < this.duration - 1) {
          this.loading = true
          this.regularWorker.postMessage({
            url: url,
            jwt: localStorage.getItem('jwt'),
            trainingId: this.trainingId,
            startSecond: Math.max(0, this.startSecond + Math.floor(this.numberOfSeconds)),
            numberOfSeconds: this.numberOfSeconds,
            nChannel: +this.nChannel
          })
        }

        if (this.startSecond - 1 > 0) {
          const prevKey = Math.floor(this.startSecond - 1) + '-' + +this.nChannel
          if (!this.extendedEcg[prevKey]) {
            this.loading = true
            this.regularWorker.postMessage({
              url: url,
              jwt: localStorage.getItem('jwt'),
              trainingId: this.trainingId,
              startSecond: Math.max(0, this.startSecond - Math.floor(this.numberOfSeconds)),
              numberOfSeconds: this.numberOfSeconds,
              nChannel: +this.nChannel
            })
          }
        }
      }
    },
    showIndicator() {
      document.getElementById('indicator').style.display = ''
    },
    hideIndicator() {
      document.getElementById('indicator').style.display = 'none'
    },
    arraySum(arr) {
      let sum = 0
      arr.forEach(item => {
        sum += item
      })
      return sum
    }
  }
}
</script>
